
import React, { useState, useMemo, useEffect } from 'react'; 
import { FavoriteBorder, Star, AddShoppingCart } from '@mui/icons-material';
import { List, ListItem, ListItemText, Checkbox, FormControl, Select, MenuItem, InputLabel } from '@mui/material';
import axios from 'axios'; 
import { useShop } from '../context/ShopContext';

const BANGLES_BANNER_IMAGE = 'https://cdnmedia-breeze.vaibhavjewellers.com/media/catalog/category/bangle-collections-web-cat-banners-vaibhavjewellers.jpg';

interface Product {
  id: number; 
  imageUrl: string;
  name: string;
  price: number;
  rating: number; 
  material: string; 
  style: string;
  occasion: string;
  color: string;
  api_category: string; 
  api_stock: number;
}

const filterOptions = {
  material: ['Gold Plated', 'Silver Plated', 'Brass', 'Copper', 'Alloy'],
  style: ['Kada', 'Set of 2', 'Set of 4', 'Thin', 'Cuff'],
  occasion: ['Wedding', 'Party Wear', 'Everyday', 'Festive'],
  color: ['Gold', 'Silver', 'Rose Gold', 'Oxidised'],
};

interface ProductCardProps {
  product: Product; 
  onAddToCart: (item: any) => Promise<void>; 
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onAddToCart }) => {
  // ✅ UPDATED ADD TO CART LOGIC
  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddToCart({
        productId: String(product.id),
        productTitle: product.name,
        productImage: product.imageUrl,
        price: product.price,
        quantity: 1
    });
  };
  
  const handleAddToWishlist = (e: React.MouseEvent) => {
    e.stopPropagation();
    alert(`Added "${product.name}" to Wishlist! (Dummy Action)`);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden transform transition-all duration-300 hover:shadow-xl hover:scale-[1.02] cursor-pointer">
      <div className="relative w-full h-72 overflow-hidden">
        <div className="w-full h-full">
          <img
            src={product.imageUrl}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
          />
        </div>
      </div>
      
      <div className="p-3 text-center">
        <span className="text-xs font-semibold uppercase text-amber-700 bg-amber-100 px-2 py-1 rounded mb-1 inline-block">
            {product.style} / {product.material}
        </span>
        <p className="block text-sm font-medium text-gray-700 whitespace-nowrap overflow-hidden text-ellipsis">
          {product.name}
        </p>
        <p className="text-lg font-bold text-amber-900 mt-1">
          ₹{product.price.toLocaleString('en-IN')}
        </p>

        <div className="flex justify-center items-center mt-1">
          <Star sx={{ fontSize: 16, color: 'gold' }} />
          <span className="text-sm text-gray-600 ml-1">{product.rating.toFixed(1)}</span>
        </div>
        
        <button 
          className="mt-3 w-full py-2 flex items-center justify-center gap-2 bg-amber-700 text-white rounded-md hover:bg-amber-800 transition"
          onClick={handleAddToCart}
        >
          <AddShoppingCart fontSize="small" />
          <span>Add To Cart</span>
        </button>
      </div>
    </div>
  );
};

const FilterSidebar: React.FC<{ 
  filters: typeof filterOptions,
  selectedFilters: Record<string, string[]>,
  onFilterChange: (category: string, value: string) => void,
}> = ({ filters, selectedFilters, onFilterChange }) => {

    const renderCheckboxes = (category: keyof typeof filterOptions, title: string) => (
        <div className="mb-4">
            <h4 className="font-semibold text-gray-700 mb-2">{title}</h4>
            <List dense className="p-0">
                {filters[category].map((item) => (
                    <ListItem key={item} disablePadding className="py-0 min-h-[30px]">
                        <Checkbox
                            size="small"
                            checked={selectedFilters[category]?.includes(item) || false}
                            onChange={() => onFilterChange(category, item)}
                        />
                        <ListItemText primary={item} className="text-sm" />
                    </ListItem>
                ))}
            </List>
        </div>
    );
    
    return (
        <div className="hidden lg:block w-64 p-4 bg-gray-50 border-r border-gray-200 sticky top-0 h-full overflow-y-auto">
          <h3 className="text-xl font-bold text-amber-900 mb-4 border-b pb-2">Filters</h3>
          {renderCheckboxes('material', 'Base Metal/Material')}
          {renderCheckboxes('style', 'Style')}
          {renderCheckboxes('occasion', 'Occasion')}
          {renderCheckboxes('color', 'Color')}
        </div>
    );
};

const BanglesPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]); 
  const [loading, setLoading] = useState(true); 
  const [error, setError] = useState<string | null>(null); 
  const [sortBy, setSortBy] = useState('latest');
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});

  const { addItemToCart } = useShop();

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      
      const mapApiItemToProduct = (item: any, metalType: string): Product => ({
          id: item.srno,
          imageUrl: item.images && item.images.length > 0
            ? item.images[0].imagePath
            : 'https://dummyimage.com/300x350/cccccc/000000&text=No+Image', 
          name: item.itemName,
          price: item.price, 
          rating: item.rating || (metalType === 'Gold' ? 4.9 : 4.5), 
          material: metalType === 'Gold' ? 'Gold Plated' : 'Silver Plated',
          style: item.itemName.toLowerCase().includes('kada') ? 'Kada' : 'Set of 2', 
          occasion: item.itemName.toLowerCase().includes('bridal') ? 'Wedding' : 'Festive',
          color: metalType === 'Gold' ? 'Gold' : 'Silver',
          api_category: item.category,
          api_stock: item.stockQuantity,
      });

      try {
        let allFetchedProducts: Product[] = [];
        const goldResponse = await axios.get<any[]>('https://sardar-jewellers.com/api/admin/gold-products');
        const goldProducts = goldResponse.data.map(item => mapApiItemToProduct(item, 'Gold'));
        allFetchedProducts = allFetchedProducts.concat(goldProducts);
  
        const silverResponse = await axios.get<any[]>('https://sardar-jewellers.com/api/admin/silver-products');
        const silverProducts = silverResponse.data.map(item => mapApiItemToProduct(item, 'Silver'));
        allFetchedProducts = allFetchedProducts.concat(silverProducts);
  
        const banglesOnly = allFetchedProducts.filter(product => 
            product.api_category?.toLowerCase() === 'bangles' || 
            product.name?.toLowerCase().includes('bangle') ||
            product.name?.toLowerCase().includes('kada')
        );
        
        setProducts(banglesOnly);

      } catch (err) {
        console.error('Error fetching bangles:', err);
        setError('Bangles load failed. Please check your backend server .');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []); 

  const filteredProducts = useMemo(() => {
    let currentProducts = products; 
    Object.keys(selectedFilters).forEach(category => {
      const selectedValues = selectedFilters[category];
      if (selectedValues && selectedValues.length > 0) {
        currentProducts = currentProducts.filter(product => {
          return selectedValues.includes(product[category as keyof Product] as string);
        });
      }
    });
    return currentProducts;
  }, [products, selectedFilters]); 

  const sortedAndFilteredProducts = useMemo(() => {
    const sorted = [...filteredProducts].sort((a, b) => {
      if (sortBy === 'price-asc') return a.price - b.price;
      if (sortBy === 'price-desc') return b.price - a.price;
      if (sortBy === 'rating-desc') return b.rating - a.rating;
      return a.id - b.id; 
    });
    return sorted;
  }, [filteredProducts, sortBy]);

  const handleFilterChange = (category: string, value: string) => {
    setSelectedFilters(prev => {
      const currentValues = prev[category] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      
      if (newValues.length === 0) {
        const { [category]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [category]: newValues };
    });
  };

  if (loading) return <div className="text-center py-20 text-xl text-amber-800">Bangles are loading ...</div>;
  if (error) return <div className="text-center py-20 text-xl text-red-600">{error}</div>;

  return (
    <div className="flex flex-col min-h-screen bg-white">
     <div 
        className="w-full h-[300px] text-white p-4 py-6 md:py-8 lg:py-10 shadow-lg relative overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `url(${BANGLES_BANNER_IMAGE})` }}
      />
      <div className="flex flex-1"> 
        <FilterSidebar 
          filters={filterOptions} 
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
        />
        <div className="flex-1 p-4 lg:p-8">
          <div className="flex justify-between items-center mb-6 border-b pb-3">
            <h1 className="text-2xl sm:text-3xl font-bold text-amber-800">Bangles</h1>
            <FormControl variant="outlined" size="small" className="min-w-[120px] bg-white">
              <InputLabel id="sort-by-label">Sort by</InputLabel>
              <Select
                labelId="sort-by-label"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as string)}
                label="Sort by"
                sx={{ 
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#b45309 !important' }, 
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#a16207 !important' }
                }} 
              >
                <MenuItem value="latest">Latest</MenuItem>
                <MenuItem value="price-asc">Price: Low to High</MenuItem>
                <MenuItem value="price-desc">Price: High to Low</MenuItem>
                <MenuItem value="rating-desc">Rating</MenuItem>
              </Select>
            </FormControl>
          </div>
          
          {sortedAndFilteredProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {sortedAndFilteredProducts.map(product => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  onAddToCart={addItemToCart} 
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-lg text-gray-600">
                No Bangles found matching your filters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BanglesPage;